import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';

import ViewMore from './ViewMore';
import RedeemButton from './RedeemButton';
import BuyButton from './BuyButton';
import AddToCartButton from './AddToCartButton';
import VariantSelector from './VariantSelector';

import Subtitle from 'ui/text/Subtitle';
import Label from 'ui/text/Label';

import { useError } from 'utils/useErrorController';
import { convertPriceFromCents, currencySymbol } from 'utils/rewards';
import { offerCategory } from 'utils/offersFormHelper';
import formattedDate from 'utils/formattedDate';

import { t } from "i18n/index";
import moment from 'moment';
import { Link } from '@mui/material';

const ButtonGroup = ({ product, onAction, onClick, requires_approval, cartMode, layout}) => {

  const { organizationSlug } = useParams();
  const { points = 0, available, start_date, redeemed_count, display_price, currency, display_discount_price, redeem_limit, category, higher_discount_percentage, discount_percentage, pending_approval} = product;

  const [selectedVariant, setSelectedVariant] = useState(null);
  const hasVariants = product.variants?.length > 0;

  const showDescription = redeemed_count > 0 && !pending_approval;
  const showRedeemButton = !display_price || points > 0;
  const redeemedMax = redeem_limit && redeemed_count >= redeem_limit
  const disableRedeemButton = redeemedMax || !available ||
    (hasVariants && (!selectedVariant || !selectedVariant.available));
  const disableBuyButton = redeemedMax || !available ||
    (hasVariants && (!selectedVariant || !selectedVariant.available));
  const buyProduct = hasVariants ? { ...product, selected_variant: selectedVariant } : product;
  const showBuyButton = display_price && currency;
  // A priced product with no currency shows neither button, so there is nothing
  // to pair the picker with — don't leave an empty block spacing the group out.
  const showPurchaseBlock = hasVariants || showRedeemButton || showBuyButton;
  const isTicket = category === offerCategory.ticket.value;
  const showHigherPercentage = higher_discount_percentage && !discount_percentage;
  const hasMinAmount = product?.min_amount && product?.min_amount > 1;
  const horizontal = layout === 'horizontal';

  const convertedPrice = convertPriceFromCents(display_price);
  const priceWithCurrency = `${parseFloat(convertedPrice)} ${currencySymbol[currency]}`;

  const convertedDiscountPrice = display_discount_price && convertPriceFromCents(display_discount_price);
  const discountPriceWithCurrency = display_discount_price && `${parseFloat(convertedDiscountPrice)} ${currencySymbol[currency]}`;

  const upcoming = start_date && moment(start_date).isAfter(moment());
  const timeToStart = upcoming && formattedDate(start_date);

  const {loading} = useError();

  const history = useHistory();

  const inlineLinkSx = {
    verticalAlign: 'baseline',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    letterSpacing: 'inherit',
  };

  if (horizontal) {
    const showRedeemedCount = showDescription && !redeemedMax && !pending_approval;
    const redeemedCountText = redeemed_count > 1
      ? t('profile:perks:redeemedNTimesShort', { count: redeemed_count })
      : t('profile:perks:redeemedOnceShort');
    const hasSublabel = showHigherPercentage || discount_percentage > 0 || redeemedMax || showRedeemedCount || pending_approval;

    const sublabels = (
      <>
        {showHigherPercentage &&
          <div className='ticket-price-sublabel'>
            <Link component="button" type="button" onClick={() => history.push(`/${organizationSlug}/memberships`)} underline="always" color="inherit" sx={inlineLinkSx}>
              {t('literal:members')}
            </Link>
            {' '}{t('profile:perks:saveUpToShort', { higher_discount_percentage })}
          </div>
        }
        {discount_percentage > 0 &&
          <div className='ticket-price-sublabel'>
            {t('profile:perks:memberRateShort', { discount_percentage })}
          </div>
        }
        {redeemedMax &&
          <div className='ticket-price-sublabel'>
            {t('profile:perks:redeemedMaxShort')}
          </div>
        }
        {showRedeemedCount &&
          <div className='ticket-price-sublabel'>{redeemedCountText}</div>
        }
        {pending_approval &&
          <div className='ticket-price-sublabel'>{t('profile:perks:pendingApproval')}</div>
        }
      </>
    );

    return (
      // The SKU picker is a precondition, not an action, so it sits on its own
      // line above the row. That keeps the action row the same shape it has on a
      // ticket card — action left, price flush right — at every width, instead
      // of a three-way squeeze that truncates all three on a phone.
      <div className='ticket-actions'>
        {hasVariants &&
          <VariantSelector
            product={product}
            selectedVariantId={selectedVariant?.id}
            onChange={setSelectedVariant}
          />
        }

        <div className='ticket-action-row'>
          {showRedeemButton &&
            <RedeemButton
              compact
              onAction={() => onAction(buyProduct, 'points')}
              loading={loading}
              disableRedeemButton={disableRedeemButton}
              points={points}
              available={available}
              timeToStart={timeToStart}
              isTicket={isTicket}
              requires_approval={requires_approval}
            />
          }

          {showBuyButton && cartMode &&
            <AddToCartButton compact product={product} variant={selectedVariant} />
          }

          {showBuyButton && !cartMode &&
            <BuyButton
              compact
              onAction={() => onAction(buyProduct, priceWithCurrency)}
              loading={loading}
              disableBuyButton={disableBuyButton}
              priceWithCurrency={priceWithCurrency}
              discountPriceWithCurrency={discountPriceWithCurrency}
              available={available}
              timeToStart={timeToStart}
              isTicket={isTicket}
              hasMinAmount={hasMinAmount}
            />
          }

          {showBuyButton ? (
            <div className='ticket-price-block'>
              <div className='ticket-price-line'>
                <Subtitle small struck={!!discountPriceWithCurrency}>{priceWithCurrency}</Subtitle>
                {discountPriceWithCurrency && <Subtitle small>{discountPriceWithCurrency}</Subtitle>}
                {hasMinAmount && <Label secondary small>{t('profile:perks:perTicket')}</Label>}
              </div>
              {sublabels}
            </div>
          ) : (
            hasSublabel && (
              <div className='ticket-price-block ticket-price-block--no-price'>
                {sublabels}
              </div>
            )
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='button-group'>
      {onClick && <ViewMore handleRedirect={onClick}/>}

      {showHigherPercentage &&
        <Label secondary small={isTicket}>
          <Link component="button" type="button" onClick={() => history.push(`/${organizationSlug}/memberships`)} underline="always" color="inherit" sx={inlineLinkSx}>
            {t('literal:members')}
          </Link>
          {' '}{t('profile:perks:saveUpToShort', {higher_discount_percentage})}
        </Label>
      }

      {discount_percentage > 0 &&
        <Label secondary small={isTicket}>
          {t('profile:perks:memberRateShort', {discount_percentage})}
        </Label>
      }

      {showDescription &&
        <Label secondary small={isTicket}>
          {redeemedMax
            ? t('profile:perks:redeemedMaxShort')
            : redeemed_count > 1
              ? t('profile:perks:redeemedNTimesShort', { count: redeemed_count })
              : t('profile:perks:redeemedOnceShort')}
        </Label>
      }

      {pending_approval &&
        <Label secondary small={isTicket}>
          {t('profile:perks:pendingApproval')}
        </Label>
      }

      {/* Choose, then pay. The picker gates the button, so the two sit together
          as one block and the labels above stay out from between them. Without a
          picker there is no pair to hold together, hence the modifier. */}
      {showPurchaseBlock &&
        <div className={hasVariants ? 'purchase-block with-picker' : 'purchase-block'}>
          {hasVariants &&
            <VariantSelector
              product={product}
              selectedVariantId={selectedVariant?.id}
              onChange={setSelectedVariant}
            />
          }

          {showRedeemButton &&
            <RedeemButton
              onAction={() => onAction(buyProduct, 'points')}
              loading={loading}
              disableRedeemButton={disableRedeemButton}
              points={points}
              available={available}
              timeToStart={timeToStart}
              isTicket={isTicket}
              requires_approval={requires_approval}
            />
          }

          {showBuyButton && cartMode &&
            <AddToCartButton product={product} variant={selectedVariant} />
          }

          {showBuyButton && !cartMode &&
            <BuyButton
              onAction={() => onAction(buyProduct, priceWithCurrency)}
              loading={loading}
              disableBuyButton={disableBuyButton}
              priceWithCurrency={priceWithCurrency}
              discountPriceWithCurrency={discountPriceWithCurrency}
              available={available}
              timeToStart={timeToStart}
              isTicket={isTicket}
              hasMinAmount={hasMinAmount}
            />
          }
        </div>
      }
    </div>
  );
};

ButtonGroup.propTypes = {
    product: PropTypes.object,
    onAction: PropTypes.func,
    onClick: PropTypes.func,
    requires_approval: PropTypes.bool,
    cartMode: PropTypes.bool,
    layout: PropTypes.oneOf(['horizontal'])
};

export default ButtonGroup;
